/**
 * Ground for a mission: heights and materials derived from the same MissionDef the sim
 * reads, so a trench on screen is a trench in the rules. The map is ringed by a margin of
 * plain ground so the camera never shows an edge.
 */

import { pondDepthAt, roadDistance, trenchDepthAt } from '../../sim/statics';
import type { MissionDef } from '../../sim/types';
import type { TerrainDef } from '../terrain/Terrain';
import { fbm, smoothstep } from '../terrain/noise';
import { riverDepthAt, riverFlattening } from './river';

export const MAP_MARGIN = 26;

/**
 * The map's edge as ground, not as an invisible wall (ROUND4 item 6). Outside the playable
 * rectangle the land climbs away over `EDGE_RUN` metres and falls back again well before the
 * margin ends, so from the air it reads as a ridge rather than a plateau, and the crest pieces
 * `world/mapEdge` lays on it stand on rising ground.
 *
 * Three sides only: the SOUTH edge (z = sizeZ) stays flat, because that is the badlands the ranger
 * drops into and walks out through when he is stranded.
 */
export const EDGE_RISE = 2.4;
export const EDGE_RUN = 7;
const EDGE_FADE_FULL = 12;
const EDGE_FADE_OUT = 20;

/** How far outside the playable rectangle a point lies on the three closed sides; 0 inside it. */
export function outsideBy(def: MissionDef, x: number, z: number): number {
  return Math.max(0, -x, x - def.sizeX, -z);
}

/** Height added to the ground outside the map. A river keeps its gap: it runs out, not into a wall. */
export function edgeRise(def: MissionDef, x: number, z: number): number {
  const out = outsideBy(def, x, z);
  if (out <= 0) return 0;
  const shape = smoothstep(0, EDGE_RUN, out) * smoothstep(EDGE_FADE_OUT, EDGE_FADE_FULL, out);
  const wobble = 0.72 + 0.56 * fbm(x * 0.07, z * 0.07, 2, def.seed + 41);
  const gap = def.waterBarriers.length > 0 ? riverFlattening(def, x, z) : 0;
  return EDGE_RISE * shape * wobble * (1 - gap);
}

export function terrainFromDef(def: MissionDef): TerrainDef {
  const relief = def.biome === 'desert' ? 0.55 : def.biome === 'arctic' ? 0.4 : 0.3;
  const seed = def.seed;
  const g = def.goalZone;
  const inside = (x: number, z: number): number =>
    smoothstep(g.x0 - 3, g.x0 + 2, x) * smoothstep(g.x1 + 3, g.x1 - 2, x) * smoothstep(g.z0 - 3, g.z0 + 2, z) * smoothstep(g.z1 + 3, g.z1 - 2, z);
  // The water plane is clipped tightly to the pond so a trench that dips below the water
  // table nearby stays dry. Rivers bring their own surface (riverSurface.ts), for the same reason.
  const pond = def.ponds[0];
  const hasRiver = def.waterBarriers.length > 0;

  return {
    originX: -MAP_MARGIN,
    originZ: -MAP_MARGIN,
    sizeX: def.sizeX + MAP_MARGIN * 2,
    sizeZ: def.sizeZ + MAP_MARGIN * 2,
    ramps: ['ground', 'groundAlt', 'dirt', 'rock'],
    baseSteps: def.biome === 'arctic' ? [3, 2, 1, 1] : [2, 1, 1, 1],
    ...(pond ? { waterLevel: -0.28, waterRegion: { x: pond.x, z: pond.z, width: pond.r * 2 + 1.5, depth: pond.r * 2 } } : {}),
    frozen: def.biome === 'arctic',
    heightAt(x, z) {
      // Flat where people build and drive; the sim treats the ground as flat everywhere. Flat
      // beside a river too: both banks must stand level above the water or it would spill.
      const built = Math.max(inside(x, z), smoothstep(3, 0.5, roadDistance(def, x, z)));
      const level = Math.max(built, hasRiver ? riverFlattening(def, x, z) : 0);
      let h = (fbm(x * 0.05, z * 0.05, 3, seed) - 0.5) * 2 * relief * (1 - level) * 0.6;
      h -= trenchDepthAt(def, x, z);
      h -= pondDepthAt(def, x, z);
      if (hasRiver) h -= riverDepthAt(def, x, z);
      return h + edgeRise(def, x, z);
    },
    materialAt(x, z) {
      const patchy = smoothstep(0.52, 0.7, fbm(x * 0.09, z * 0.09, 2, seed + 11));
      const road = smoothstep(1.2, 0, roadDistance(def, x, z));
      const yard = inside(x, z) * 0.85;
      const trench = smoothstep(0.1, 0.6, trenchDepthAt(def, x, z));
      // Ponds and rivers share a shore: stones where the bank dips under, which also paves the fords.
      const depth = Math.max(pondDepthAt(def, x, z), hasRiver ? riverDepthAt(def, x, z) : 0);
      const bank = smoothstep(0.0, 0.3, depth) * smoothstep(0.9, 0.3, depth);
      const stony = smoothstep(0.74, 0.86, fbm(x * 0.13, z * 0.13, 2, seed + 23)) * (def.biome === 'desert' ? 0.55 : 1);
      const dirt = Math.max(road, yard, trench * 1.4);
      // Scree and broken rock take over outside the map, so the ridge reads as ground you do not walk on.
      const scree = smoothstep(0.5, EDGE_RUN * 0.6, outsideBy(def, x, z));
      const rockW = Math.max(bank * 1.2, stony * 0.9, scree * 1.5);
      return [Math.max(0.05, 1 - patchy - dirt - rockW), patchy * (1 - dirt), dirt, rockW];
    },
  };
}
